import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaMicrophone, FaStop, FaUpload, FaUndo, FaCheckCircle, FaExclamationTriangle, FaRedo, FaPen, FaMagic } from "react-icons/fa";
import AdminModal from "../AdminModal";
import StatusBadge from "../StatusBadge";
import FormField from "../FormField";
import { colors, spacing, typography, radius, btnPrimary, btnSecondary, transitions, inputBase } from "../../utils/designTokens";
import { actaAdmin, mensajeDeError, subirAUrlFirmada } from "../../utils/asambleas/api";
import {
  ACCEPT_AUDIO,
  LIMITES_AUDIO,
  MAX_MINUTA_TEXTO,
  cambiosDeMinuta,
  contentTypeDeArchivo,
  elegirMimeGrabacion,
  formatoBytes,
  formatoTiempo,
  mimeBase,
  minutaBloqueaActa,
  minutaEnCurso,
  ESTADOS_MINUTA,
} from "./actaFlujo";

// "Ajustar con minuta": el staff escribe las correcciones, las graba
// (MediaRecorder) o sube un audio corto ("el punto 3 se aprobó por mayoría…").
// El audio se sube por URL firmada y el worker lo transcribe; el texto va
// directo (minutaTexto). En los dos casos el worker aplica operaciones
// validadas sobre el acta. Este modal hace la captura y el envío; el
// seguimiento (polling de estadoMinuta), la recarga del acta y "Deshacer" los
// maneja el Editor, que sigue aunque se cierre el modal.
//
// Props:
//   open, onClose
//   actaId
//   minuta            estado que sigue el Editor: { minutaId, estado, operaciones, aplicadas, cambios,
//                     requiereAclaracion, revisionId, ultimoError, deshecha, workerApagado } | null
//   acta, edificio    para describir los cambios
//   copropietarios    para nombrar a las personas en los cambios (votos, firmas)
//   asegurarGuardado  async () => boolean  guarda los cambios pendientes antes de subir
//   onEnviada         ({ minutaId, workerHabilitado, origen }) => void   (workerHabilitado: lo que dijo aplicarMinuta;
//                     origen: "audio" | "texto")
//   onDeshacer        (revisionId) => void
//   puedeDeshacer     false si el acta cambió después de la minuta (ya no se puede deshacer)
//   deshaciendo       boolean
//   onNueva           () => void  descarta el resultado anterior para grabar otra

const MAX_GRABACION_SEG = 15 * 60;
const ESTADO_BADGE = { subida: "pendiente", pendiente: "pendiente", procesando: "en_proceso", aplicada: "aplicado", error: "error" };

export default function ActaMinutaModal({
  open,
  onClose,
  actaId,
  minuta,
  acta,
  edificio,
  copropietarios = null,
  asegurarGuardado,
  onEnviada,
  onDeshacer,
  puedeDeshacer = true,
  deshaciendo = false,
  onNueva,
}) {
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [fuente, setFuente] = useState(null); // { blob, contentType, nombre, duracionMs? }
  // Minuta escrita: el texto sobrevive a cerrar el modal (no se pierde lo tipeado).
  const [escribiendo, setEscribiendo] = useState(false);
  const [texto, setTexto] = useState("");
  const [subiendo, setSubiendo] = useState(null); // { pct (null = sin barra), paso, cancelable }
  const [error, setError] = useState("");

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const inicioRef = useRef(0);
  const abortRef = useRef(null);
  const archivoRef = useRef(null);
  const textoRef = useRef(null);

  const soportaGrabar =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

  // Corta y DESCARTA la grabación en curso (al cerrar o desmontar): sin los
  // handlers, el onstop no vuelve a cargar el audio después de cerrar.
  const cortarMicrofono = () => {
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      rec.ondataavailable = null;
      rec.onstop = null;
      try {
        rec.stop();
      } catch (_) {
        /* ya estaba parado */
      }
    }
    chunksRef.current = [];
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Al cerrar o desmontar: soltar el micrófono y cancelar una subida en curso.
  useEffect(() => {
    if (open) return undefined;
    cortarMicrofono();
    if (abortRef.current) abortRef.current.abort();
    setGrabando(false);
    setFuente(null);
    setError("");
    return undefined;
  }, [open]);
  useEffect(
    () => () => {
      cortarMicrofono();
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  // Reloj de la grabación + corte automático.
  useEffect(() => {
    if (!grabando) return undefined;
    const t = setInterval(() => {
      const s = Math.floor((Date.now() - inicioRef.current) / 1000);
      setSegundos(s);
      if (s >= MAX_GRABACION_SEG) detenerGrabacion();
    }, 500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grabando]);

  useEffect(() => {
    if (open && escribiendo && textoRef.current) textoRef.current.focus();
  }, [open, escribiendo]);

  // URL local para escuchar lo grabado o elegido antes de mandarlo.
  const urlPrevia = useMemo(() => {
    if (!fuente || typeof URL.createObjectURL !== "function") return null;
    return URL.createObjectURL(fuente.blob);
  }, [fuente]);
  useEffect(
    () => () => {
      if (urlPrevia && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(urlPrevia);
    },
    [urlPrevia]
  );

  const iniciarGrabacion = async () => {
    setError("");
    if (!soportaGrabar) {
      setError("Este navegador no permite grabar. Subí un archivo de audio.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (_) {
      setError("No tenemos permiso para usar el micrófono. Habilitalo en el navegador o subí un archivo.");
      return;
    }
    const mime = elegirMimeGrabacion(window.MediaRecorder);
    let rec;
    try {
      rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    } catch (_) {
      stream.getTracks().forEach((t) => t.stop());
      setError("No se pudo empezar a grabar. Probá subir un archivo.");
      return;
    }
    // Recién con el micrófono andando se descarta el audio anterior (si se niega el permiso, queda).
    setFuente(null);
    setEscribiendo(false);
    chunksRef.current = [];
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size) chunksRef.current.push(ev.data);
    };
    rec.onstop = () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const contentType = mimeBase(rec.mimeType || mime) || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: contentType });
      chunksRef.current = [];
      setGrabando(false);
      if (blob.size > 0) {
        setFuente({ blob, contentType, nombre: "Grabación", duracionMs: Date.now() - inicioRef.current });
      } else {
        setError("La grabación quedó vacía. Probá de nuevo.");
      }
    };
    recRef.current = rec;
    streamRef.current = stream;
    inicioRef.current = Date.now();
    setSegundos(0);
    rec.start(1000);
    setGrabando(true);
  };

  function detenerGrabacion() {
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  }

  const elegirArchivo = (file) => {
    setError("");
    setFuente(null);
    if (!file) return;
    const contentType = contentTypeDeArchivo(file);
    if (!contentType) {
      setError("Ese formato no se admite. Usá mp3, m4a, wav, ogg, opus, webm, aac o mp4.");
      return;
    }
    if (file.size > LIMITES_AUDIO.bytesMinuta) {
      setError(`La minuta no puede pasar de ${formatoBytes(LIMITES_AUDIO.bytesMinuta)}.`);
      return;
    }
    setEscribiendo(false);
    setFuente({ blob: file, contentType, nombre: file.name });
  };

  // Un camino por vez: escribir descarta el audio grabado o elegido.
  const elegirEscribir = () => {
    setError("");
    setFuente(null);
    setEscribiendo(true);
    if (textoRef.current) textoRef.current.focus();
  };

  const textoListo = texto.trim().length > 0 && texto.length <= MAX_MINUTA_TEXTO;

  const enviarTexto = async () => {
    if (!textoListo || subiendo) return;
    setError("");
    try {
      setSubiendo({ pct: null, paso: "Guardando el acta…" });
      // La minuta se aplica sobre la versión guardada: primero los cambios pendientes.
      const ok = asegurarGuardado ? await asegurarGuardado() : true;
      if (!ok) return;
      setSubiendo({ pct: null, paso: "Enviando a procesar…" });
      const r = (await actaAdmin("minutaTexto", { actaId, texto })) || {};
      if (!r.minutaId) throw new Error("El servidor no devolvió la minuta.");
      setTexto("");
      onEnviada && onEnviada({ minutaId: r.minutaId, workerHabilitado: r.workerHabilitado, origen: "texto" });
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setSubiendo(null);
    }
  };

  const enviar = async () => {
    if (escribiendo) {
      await enviarTexto();
      return;
    }
    if (!fuente || subiendo) return;
    if (fuente.blob.size > LIMITES_AUDIO.bytesMinuta) {
      setError(`La minuta no puede pasar de ${formatoBytes(LIMITES_AUDIO.bytesMinuta)}.`);
      return;
    }
    setError("");
    abortRef.current = typeof AbortController !== "undefined" ? new AbortController() : null;
    try {
      setSubiendo({ pct: 0, paso: "Guardando el acta…", cancelable: true });
      // La minuta se aplica sobre la versión guardada: primero los cambios pendientes.
      const ok = asegurarGuardado ? await asegurarGuardado() : true;
      if (!ok) return;
      setSubiendo({ pct: 0, paso: "Subiendo la minuta…", cancelable: true });
      const sub = await actaAdmin("subirMinuta", { actaId, contentType: fuente.contentType, bytes: fuente.blob.size });
      if (!sub || !sub.uploadUrl || !sub.minutaId) throw new Error("El servidor no devolvió dónde subir la minuta.");
      await subirAUrlFirmada(sub.uploadUrl, fuente.blob, {
        contentType: fuente.contentType,
        headers: sub.headers || {},
        signal: abortRef.current ? abortRef.current.signal : undefined,
        onProgreso: (pct) => setSubiendo({ pct, paso: "Subiendo la minuta…", cancelable: true }),
      });
      setSubiendo({ pct: 100, paso: "Enviando a procesar…" });
      const ap = (await actaAdmin("aplicarMinuta", { actaId, minutaId: sub.minutaId })) || {};
      setFuente(null);
      onEnviada && onEnviada({ minutaId: sub.minutaId, workerHabilitado: ap.workerHabilitado, origen: "audio" });
    } catch (e) {
      if (e && e.name === "AbortError") setError("Se canceló la subida.");
      else setError(mensajeDeError(e));
    } finally {
      setSubiendo(null);
      abortRef.current = null;
    }
  };

  const cerrar = () => {
    if (grabando) cortarMicrofono();
    onClose && onClose();
  };

  const enCurso = minutaEnCurso(minuta);
  const terminada = minuta && (minuta.estado === "aplicada" || minuta.estado === "error");

  let footer;
  if (enCurso && !minutaBloqueaActa(minuta)) {
    // En cola con el worker apagado: no hay nada que esperar acá.
    footer = (
      <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={cerrar}>
        Cerrar
      </button>
    );
  } else if (terminada) {
    footer = (
      <>
        <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={onNueva}>
          <FaRedo /> Hacer otra minuta
        </button>
        <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={cerrar}>
          Listo
        </button>
      </>
    );
  } else if (enCurso) {
    footer = (
      <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={cerrar}>
        Cerrar (sigue en segundo plano)
      </button>
    );
  } else {
    const listo = escribiendo ? textoListo : !!fuente;
    const cancelarSubida = subiendo && subiendo.cancelable;
    footer = (
      <>
        <button
          type="button"
          style={{ ...btnSecondary, opacity: subiendo && !cancelarSubida ? 0.6 : 1 }}
          className="habitia-btn-secondary"
          onClick={cancelarSubida ? () => abortRef.current && abortRef.current.abort() : cerrar}
          disabled={!!subiendo && !cancelarSubida}
        >
          {cancelarSubida ? "Cancelar subida" : "Cancelar"}
        </button>
        <button
          type="button"
          style={{ ...btnPrimary, opacity: !listo || subiendo || grabando ? 0.6 : 1 }}
          className="habitia-btn-primary"
          onClick={enviar}
          disabled={!listo || !!subiendo || grabando}
        >
          {escribiendo ? <FaMagic /> : <FaUpload />}{" "}
          {subiendo ? (escribiendo ? "Enviando…" : "Subiendo…") : "Aplicar minuta"}
        </button>
      </>
    );
  }

  return (
    <AdminModal
      open={open}
      onClose={subiendo ? () => {} : cerrar}
      title="Ajustar con minuta"
      subtitle="Escribí o contá en voz alta qué hay que corregir; la IA aplica los cambios al acta."
      size="medium"
      icon={<FaMagic />}
      footer={<div style={S.footer}>{footer}</div>}
    >
      {minuta ? (
        <ResultadoMinuta
          minuta={minuta}
          acta={acta}
          edificio={edificio}
          copropietarios={copropietarios}
          onDeshacer={onDeshacer}
          puedeDeshacer={puedeDeshacer}
          deshaciendo={deshaciendo}
        />
      ) : (
        <>
          <p style={S.ayuda}>
            Ejemplo: “En el punto 3 votaron en contra los de la unidad 402. La hora de cierre fue 21:15.” Nombrá el punto
            o la unidad para que la IA no tenga que adivinar.
          </p>

          {/* Tres caminos y uno solo a la vez: escribir, grabar acá o subir un audio ya grabado. */}
          {grabando ? (
            <div style={S.grabador}>
              <span style={S.reloj} aria-live="polite">
                <span style={S.punto} aria-hidden="true" /> Grabando {formatoTiempo(segundos * 1000)}
              </span>
              <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={detenerGrabacion}>
                <FaStop /> Terminar
              </button>
            </div>
          ) : (
            <div style={S.grabador}>
              <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={elegirEscribir} disabled={!!subiendo}>
                <FaPen /> Escribir los cambios
              </button>
              <button
                type="button"
                style={{ ...btnSecondary, opacity: soportaGrabar ? 1 : 0.6 }}
                className="habitia-btn-secondary"
                onClick={iniciarGrabacion}
                disabled={!!subiendo}
              >
                <FaMicrophone /> {fuente ? "Grabar de nuevo" : "Grabar ahora"}
              </button>
              <button
                type="button"
                style={btnSecondary}
                className="habitia-btn-secondary"
                onClick={() => archivoRef.current && archivoRef.current.click()}
                disabled={!!subiendo}
              >
                <FaUpload /> {fuente ? "Elegir otro audio" : "Subir un audio"}
              </button>
              <input
                ref={archivoRef}
                id="acta-minuta-archivo"
                type="file"
                accept={`audio/*,${ACCEPT_AUDIO}`}
                aria-label="Archivo de audio"
                tabIndex={-1}
                style={S.archivoOculto}
                onChange={(e) => {
                  elegirArchivo(e.target.files && e.target.files[0]);
                  e.target.value = "";
                }}
                disabled={!!subiendo}
              />
            </div>
          )}
          {!grabando && !fuente && !escribiendo && (
            <p style={{ ...S.hint, margin: `${spacing[2]}px 0 0` }}>
              Grabación de hasta {MAX_GRABACION_SEG / 60} minutos, o un audio mp3, m4a, wav, ogg, opus, webm, aac o mp4 de
              hasta {formatoBytes(LIMITES_AUDIO.bytesMinuta)}.
            </p>
          )}

          {escribiendo && !grabando && (
            <FormField
              label="Qué hay que cambiar"
              htmlFor="acta-minuta-texto"
              hint={
                texto.length > MAX_MINUTA_TEXTO * 0.8
                  ? `${texto.length.toLocaleString("es-UY")} de ${MAX_MINUTA_TEXTO.toLocaleString("es-UY")} caracteres`
                  : "Podés poner un cambio por renglón."
              }
              style={{ marginTop: spacing[4] }}
            >
              <textarea
                ref={textoRef}
                id="acta-minuta-texto"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  // Ctrl/⌘ + Enter aplica, como en otros formularios largos.
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    enviar();
                  }
                }}
                rows={6}
                maxLength={MAX_MINUTA_TEXTO}
                placeholder={"En el punto 3 votaron en contra los de la 402.\nLa hora de cierre fue 21:15."}
                style={S.textarea}
                disabled={!!subiendo}
              />
            </FormField>
          )}

          {fuente && (
            <div style={S.previa}>
              <span style={S.hint}>
                {fuente.nombre} · {formatoBytes(fuente.blob.size)}
                {fuente.duracionMs ? ` · ${formatoTiempo(fuente.duracionMs)}` : ""}
              </span>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              {urlPrevia && <audio controls src={urlPrevia} style={{ width: "100%" }} aria-label="Escuchar la minuta antes de enviarla" />}
            </div>
          )}

          {subiendo && (
            <div style={S.progreso}>
              <span style={S.hint}>{subiendo.paso}</span>
              {subiendo.pct != null && <Barra pct={subiendo.pct} etiqueta="Progreso de la subida de la minuta" />}
            </div>
          )}

          {error && (
            <p style={S.error} role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </AdminModal>
  );
}

function ResultadoMinuta({ minuta, acta, edificio, copropietarios, onDeshacer, puedeDeshacer, deshaciendo }) {
  const estado = minuta.estado || "pendiente";
  if (minutaEnCurso(minuta) && !minutaBloqueaActa(minuta)) {
    return (
      <div style={S.columna}>
        <StatusBadge status="pendiente" label="En cola" size="sm" />
        <p style={S.texto}>
          La minuta quedó guardada, pero el procesamiento automático todavía no está activo: se aplica cuando se habilite.
          Mientras tanto podés seguir editando el acta; vas a ver los cambios de la minuta al volver a abrirla.
        </p>
      </div>
    );
  }
  if (minutaEnCurso(minuta)) {
    return (
      <div style={S.columna}>
        <StatusBadge status={ESTADO_BADGE[estado] || "pendiente"} label={ESTADOS_MINUTA[estado] || estado} size="sm" />
        <p style={S.texto}>
          {minuta.origen === "texto" ? "Estamos aplicando los cambios de la minuta." : "Estamos transcribiendo la minuta y aplicando los cambios."}{" "}
          Suele tardar uno o dos minutos. Mientras tanto el acta queda en solo lectura para que no se pisen las ediciones.
        </p>
      </div>
    );
  }
  if (estado === "error") {
    return (
      <div style={S.columna}>
        <StatusBadge status="error" label="No se aplicó" size="sm" />
        <p style={S.texto}>
          {minuta.ultimoError ? `No se pudo aplicar la minuta: ${minuta.ultimoError}` : "No se pudo aplicar la minuta. Probá de nuevo."}
        </p>
      </div>
    );
  }
  const aplicadas = Array.isArray(minuta.aplicadas) ? minuta.aplicadas : [];
  const operaciones = Array.isArray(minuta.operaciones) ? minuta.operaciones : [];
  const noAplicadas = Math.max(0, operaciones.length - aplicadas.length);
  const aclaraciones = Array.isArray(minuta.requiereAclaracion) ? minuta.requiereAclaracion.filter(Boolean) : [];
  // Cada operación con su antes → después (los cambios de la revisión, si el servidor los mandó).
  const lista = cambiosDeMinuta(minuta, acta, edificio, copropietarios);
  const n = aplicadas.length || lista.length;
  return (
    <div style={S.columna}>
      <p style={{ ...S.texto, display: "flex", alignItems: "center", gap: spacing[2] }}>
        <FaCheckCircle color={colors.success} aria-hidden="true" />
        {minuta.deshecha
          ? "Deshiciste los cambios de esta minuta."
          : n
            ? `Se ${n === 1 ? "aplicó 1 cambio" : `aplicaron ${n} cambios`} al acta.`
            : "La minuta no generó cambios."}
      </p>
      {lista.length > 0 && (
        <ul style={S.lista} aria-label="Cambios aplicados">
          {lista.map((d, i) => (
            <li key={i} style={{ ...S.cambio, ...(minuta.deshecha ? { opacity: 0.6 } : {}) }}>
              <div style={S.cambioHead}>
                <span style={S.cambioTitulo}>{d.titulo}</span>
                {d.donde && <span style={S.hint}>{d.donde}</span>}
                {d.confianza === "baja" && <StatusBadge status="pendiente" label="Confianza baja" size="sm" />}
              </div>
              {(d.antes != null || d.despues != null) && (
                <span style={S.texto}>
                  {d.antes != null ? `${d.antes} → ` : ""}
                  {d.despues != null ? d.despues : ""}
                </span>
              )}
              {d.detalles.length > 0 && (
                <ul style={S.detalles}>
                  {d.detalles.map((x, k) => (
                    <li key={k} style={S.texto}>
                      {x.campo ? `${x.campo}: ` : ""}
                      {x.antes != null ? `${x.antes} → ` : ""}
                      {x.despues != null ? x.despues : ""}
                    </li>
                  ))}
                </ul>
              )}
              {d.cita && <span style={S.cita}>“{d.cita}”</span>}
            </li>
          ))}
        </ul>
      )}
      {noAplicadas > 0 && (
        <p style={S.hint}>
          {noAplicadas === 1 ? "1 indicación no se aplicó" : `${noAplicadas} indicaciones no se aplicaron`} porque no se
          pudieron validar contra el acta.
        </p>
      )}
      {aclaraciones.length > 0 && (
        <div style={S.aclaracion}>
          <span style={{ ...S.cambioTitulo, display: "flex", alignItems: "center", gap: spacing[2] }}>
            <FaExclamationTriangle color={colors.warningDark} aria-hidden="true" /> Para aclarar a mano
          </span>
          <ul style={{ margin: 0, paddingLeft: spacing[5] }}>
            {aclaraciones.map((t, i) => (
              <li key={i} style={S.texto}>
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}
      {n > 0 && minuta.revisionId && !minuta.deshecha && onDeshacer && puedeDeshacer && (
        <button
          type="button"
          style={{ ...btnSecondary, alignSelf: "flex-start" }}
          className="habitia-btn-secondary"
          onClick={() => onDeshacer(minuta.revisionId)}
          disabled={deshaciendo}
        >
          <FaUndo /> {deshaciendo ? "Deshaciendo…" : "Deshacer estos cambios"}
        </button>
      )}
      {n > 0 && minuta.revisionId && !minuta.deshecha && onDeshacer && !puedeDeshacer && (
        <p style={S.hint}>El acta se editó después de esta minuta: sus cambios ya no se pueden deshacer automáticamente.</p>
      )}
    </div>
  );
}

function Barra({ pct, etiqueta }) {
  const v = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)));
  return (
    <div style={S.barra} role="progressbar" aria-label={etiqueta} aria-valuemin={0} aria-valuemax={100} aria-valuenow={v}>
      <div style={{ ...S.barraRelleno, width: `${v}%` }} />
    </div>
  );
}

const S = {
  footer: { display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%", flexWrap: "wrap" },
  ayuda: { marginTop: 0, fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.5 },
  grabador: { display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" },
  // Fuera de la vista pero en el DOM: lo abre el botón "Subir un audio".
  archivoOculto: { position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden", pointerEvents: "none" },
  reloj: { display: "inline-flex", alignItems: "center", gap: spacing[2], fontSize: typography.sizes.md, color: colors.errorDark, fontVariantNumeric: "tabular-nums" },
  punto: { width: 10, height: 10, borderRadius: radius.full, background: colors.error },
  hint: { fontSize: typography.sizes.sm, color: colors.gray600 },
  previa: { display: "flex", flexDirection: "column", gap: spacing[2], marginTop: spacing[4] },
  textarea: { ...inputBase, resize: "vertical", minHeight: 132, lineHeight: 1.5 },
  progreso: { display: "flex", flexDirection: "column", gap: spacing[1], marginTop: spacing[4] },
  barra: { height: 8, background: colors.gray100, borderRadius: radius.full, overflow: "hidden" },
  barraRelleno: { height: "100%", background: colors.primary, transition: `width ${transitions.normal}` },
  error: { marginBottom: 0, marginTop: spacing[3], fontSize: typography.sizes.base, color: colors.errorDark },
  columna: { display: "flex", flexDirection: "column", gap: spacing[3], alignItems: "flex-start" },
  texto: { margin: 0, fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.5 },
  lista: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[2], width: "100%" },
  cambio: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
    background: colors.gray50,
    borderRadius: radius.sm,
    padding: `${spacing[2]}px ${spacing[3]}px`,
  },
  cambioHead: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  detalles: { margin: 0, paddingLeft: spacing[5], display: "flex", flexDirection: "column", gap: 2 },
  cambioTitulo: { fontSize: typography.sizes.md, color: colors.black },
  cita: { fontSize: typography.sizes.base, color: colors.gray700, fontStyle: "italic" },
  aclaracion: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
    background: colors.warningLight,
    borderRadius: radius.sm,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    width: "100%",
  },
};
